import { useEffect, useMemo, useState } from "react";
import AuthUser from "./AuthUser";
import toast, { Toaster } from "react-hot-toast";
import TaskSkeleton from "./TaskSkeleton";
import * as XLSX from "xlsx";
import "./ExpenseModule.css";

const emptyForm = {
    name: "",
    contact_person: "",
    phone: "",
    email: "",
    address: "",
    gst_no: "",
    description: "",
};

export default function ExpenseVendorMasterList() {
    const { http, user } = AuthUser();
    const [vendors, setVendors] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [nameFilter, setNameFilter] = useState("");

    useEffect(() => { fetchVendors(); }, []);

    const fetchVendors = async () => {
        setIsLoading(true);
        try {
            const res = await http.get(`/expense_vendor_masters?login_user=${user.id}`);
            if (res.data.success) setVendors(res.data.data || []);
        } catch (err) {
            console.error(err);
            toast.error("Error fetching vendor masters");
        } finally {
            setIsLoading(false);
        }
    };

    const openAddModal = () => {
        setEditId(null);
        setForm(emptyForm);
        setShowModal(true);
    };

    const openEditModal = (vendor) => {
        setEditId(vendor.id);
        setForm({
            name: vendor.name || "",
            contact_person: vendor.contact_person || "",
            phone: vendor.phone || "",
            email: vendor.email || "",
            address: vendor.address || "",
            gst_no: vendor.gst_no || "",
            description: vendor.description || "",
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditId(null);
        setForm(emptyForm);
    };

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const buildPayload = () => ({
        name: form.name.trim(),
        contact_person: form.contact_person.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        address: form.address.trim() || null,
        gst_no: form.gst_no.trim() || null,
        description: form.description.trim() || null,
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) {
            toast.error("Please enter vendor name");
            return;
        }
        setSaving(true);
        try {
            const payload = buildPayload();
            const res = editId
                ? await http.put(`/expense_vendor_master/${editId}?login_user=${user.id}`, payload)
                : await http.post(`/expense_vendor_master?login_user=${user.id}`, payload);

            if (res.data.success) {
                toast.success(editId ? "Vendor updated!" : "Vendor added!");
                closeModal();
                fetchVendors();
            } else {
                toast.error(res.data.message || "Error saving vendor");
            }
        } catch (err) {
            toast.error(err.response?.data?.message || "Error saving vendor");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this vendor master?")) return;
        try {
            const res = await http.delete(`/expense_vendor_master/${id}`);
            if (res.data.success) {
                toast.success("Deleted!");
                fetchVendors();
            }
        } catch (err) {
            toast.error("Error deleting");
        }
    };

    const filteredVendors = useMemo(() => {
        const normalizedName = nameFilter.trim().toLowerCase();
        return vendors
            .filter((vendor) => {
                if (!normalizedName) return true;
                return (vendor.name || "").toLowerCase().includes(normalizedName);
            })
            .sort((a, b) =>
                (a.name || "").localeCompare(b.name || "", undefined, {
                    sensitivity: "base",
                    numeric: true,
                })
            );
    }, [vendors, nameFilter]);

    const clearFilters = () => {
        setNameFilter("");
    };

    const handleExport = () => {
        const exportData = filteredVendors.map((vendor, index) => ({
            No: index + 1,
            Name: vendor.name || "",
            Contact: vendor.contact_person || "",
            Phone: vendor.phone || "",
            Email: vendor.email || "",
            "GST No": vendor.gst_no || "",
            Address: vendor.address || "",
            Description: vendor.description || "",
        }));
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Vendor Master");
        XLSX.writeFile(
            workbook,
            `Expense_Vendor_Master_${new Date().toISOString().slice(0, 10)}.xlsx`
        );
        toast.success("Vendor master exported successfully!");
    };

    return (
        <>
            <Toaster />
            <div className="container mt-3 expense-module">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold mb-0">Vendor Master</h5>
                    <div className="d-flex gap-2">
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-success"
                            onClick={handleExport}
                            disabled={filteredVendors.length === 0}
                        >
                            <i className="fas fa-file-excel me-1"></i>Export
                        </button>
                        <button type="button" className="btn btn-sm btn-success" onClick={openAddModal}>
                            <i className="fas fa-plus me-1"></i>Add Vendor
                        </button>
                    </div>
                </div>

                <div className="card mb-3">
                    <div className="card-body py-2">
                        <div className="row g-2 align-items-end">
                            <div className="col-md-4">
                                <label className="form-label small mb-1">Name</label>
                                <input
                                    type="search"
                                    className="form-control form-control-sm"
                                    value={nameFilter}
                                    onChange={(e) => setNameFilter(e.target.value)}
                                    placeholder="Search vendor name..."
                                />
                            </div>
                            <div className="col-md-8 d-flex justify-content-between align-items-center">
                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearFilters}>
                                    <i className="fas fa-times me-1"></i>Clear
                                </button>
                                <span className="badge bg-secondary">{filteredVendors.length} records</span>
                            </div>
                        </div>
                    </div>
                </div>

                {isLoading ? <TaskSkeleton /> : (
                    <div className="table-responsive">
                        <table className="table table-striped table-sm">
                            <thead className="table-primary">
                                <tr>
                                    <th style={{ width: "50px" }}>No</th>
                                    <th>Name</th>
                                    <th>Contact</th>
                                    <th>Phone</th>
                                    <th>Email</th>
                                    <th>GST No</th>
                                    <th>Address</th>
                                    <th>Description</th>
                                    <th style={{ width: "120px" }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredVendors.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="text-center text-muted py-3">
                                            {vendors.length === 0
                                                ? "No vendors yet. Click Add Vendor to create one."
                                                : "No vendors match the selected filters."}
                                        </td>
                                    </tr>
                                ) : filteredVendors.map((vendor, idx) => (
                                    <tr key={vendor.id}>
                                        <td>{idx + 1}</td>
                                        <td className="fw-semibold">{vendor.name}</td>
                                        <td>{vendor.contact_person || "—"}</td>
                                        <td>{vendor.phone || "—"}</td>
                                        <td>{vendor.email || "—"}</td>
                                        <td>{vendor.gst_no || "—"}</td>
                                        <td>{vendor.address || "—"}</td>
                                        <td>{vendor.description || "—"}</td>
                                        <td>
                                            <div className="d-flex gap-1">
                                                <button className="btn btn-sm btn-outline-primary px-2" onClick={() => openEditModal(vendor)} title="Edit">
                                                    <i className="fas fa-edit"></i>
                                                </button>
                                                <button className="btn btn-sm btn-outline-danger px-2" onClick={() => handleDelete(vendor.id)} title="Delete">
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {showModal && (
                <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header py-2" style={{ background: "linear-gradient(135deg, #1a237e 0%, #283593 100%)", color: "#fff" }}>
                                <h6 className="modal-title mb-0">
                                    <i className="fas fa-truck me-2"></i>
                                    {editId ? "Edit Vendor" : "Add Vendor"}
                                </h6>
                                <button type="button" className="btn-close btn-close-white" onClick={closeModal}></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label small fw-semibold">Vendor Name *</label>
                                            <input type="text" name="name" className="form-control form-control-sm" value={form.name} onChange={handleChange} placeholder="Vendor name" required autoFocus />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label small fw-semibold">Contact Person</label>
                                            <input type="text" name="contact_person" className="form-control form-control-sm" value={form.contact_person} onChange={handleChange} placeholder="Contact person" />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label small fw-semibold">Phone</label>
                                            <input type="text" name="phone" className="form-control form-control-sm" value={form.phone} onChange={handleChange} placeholder="Phone number" />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label small fw-semibold">Email</label>
                                            <input type="email" name="email" className="form-control form-control-sm" value={form.email} onChange={handleChange} placeholder="Email address" />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label small fw-semibold">GST No</label>
                                            <input type="text" name="gst_no" className="form-control form-control-sm" value={form.gst_no} onChange={handleChange} placeholder="GST number" />
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label small fw-semibold">Address</label>
                                            <input type="text" name="address" className="form-control form-control-sm" value={form.address} onChange={handleChange} placeholder="Address" />
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label small fw-semibold">Description</label>
                                            <textarea name="description" className="form-control form-control-sm" rows={2} value={form.description} onChange={handleChange} placeholder="Notes or description (optional)" />
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer py-2">
                                    <button type="button" className="btn btn-sm btn-secondary" onClick={closeModal} disabled={saving}>Cancel</button>
                                    <button type="submit" className="btn btn-sm btn-primary" disabled={saving}>
                                        {saving ? (
                                            <><span className="spinner-border spinner-border-sm me-1"></span>Saving...</>
                                        ) : (
                                            <><i className="fas fa-check me-1"></i>{editId ? "Update" : "Save"}</>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
